import { HttpErrorResponse } from '@angular/common/http';
import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { HotToastService } from '@ngneat/hot-toast';
import { ApplyForBusinessAccountRequest, CountryDto, RegionDto } from 'src/app/core/dtos/dto';
import { HttpErrorResponseMessages } from 'src/app/core/dtos/http-error-response-messages';
import { AccountService } from 'src/app/core/services/account.service';
import { CountryRegionService } from 'src/app/core/services/country-region.service';
import { ConfigHelper } from 'src/app/core/services/helpers/config.helper';

@Component({
  selector: 'app-apply-business-account',
  templateUrl: './apply-business-account.component.html',
  styleUrls: ['./apply-business-account.component.scss']
})
export class ApplyBusinessAccountComponent implements OnInit {
  userForm: FormGroup;
  brandName: string;
  namePattern = '[\\u0000-\\u007f]+';

  salutations: string[];
  industries: string[];
  countries: CountryDto[];
  regions: RegionDto[] = [];

  get f() { return this.userForm.controls; }

  constructor(
    private formBuilder: FormBuilder,
    private accountService: AccountService,
    private configHelper: ConfigHelper,
    private toastService: HotToastService,
    private regionService: CountryRegionService,
    private router: Router,
  ) { }

  ngOnInit(): void {
    this.brandName = this.configHelper.getBrandName();
    this.getIndustries();
    this.getSalutations();
    this.getCountries();
    this.userForm = this.formBuilder.group({
      companyName: ['', Validators.required],
      industry: [null, Validators.required],
      otherIndustry: [''],
      country: [null, Validators.required],
      addressLine1: ['', Validators.required],
      addressLine2: [''],
      city: ['', Validators.required],
      region: ['', Validators.required],
      postalCode: ['', Validators.required],
      contactFirstName: ['', [Validators.required, Validators.pattern(this.namePattern)]],
      contactLastName: ['', [Validators.required, Validators.pattern(this.namePattern)]],
      salutation: [null, Validators.required],
      title: [''],
      email: ['', [Validators.required, Validators.email]],
      phone: ['', Validators.required],
    });
  }

  getCountries() {
    this.regionService.getCountries()
      .subscribe(
        result => {
          this.countries = result.content;
        },
        (error: HttpErrorResponse) => {
          if (error.status === 400) {
            const errorMessage = error.error as HttpErrorResponseMessages;
            errorMessage.reasons.forEach(reasons => {
              this.toastService.warning(reasons.errorMessage);
            });
          }
        });
  }

  getSalutations() {
    this.accountService.getSalutations()
      .subscribe(
        result => {
          this.salutations = result.content;
        },
        (error: HttpErrorResponse) => {
          if (error.status === 400) {
            const errorMessage = error.error as HttpErrorResponseMessages;
            errorMessage.reasons.forEach(reasons => {
              this.toastService.warning(reasons.errorMessage);
            });
          }
        });
  }

  getIndustries() {
    this.accountService.getBusinessAccountIndustries()
      .subscribe(
        result => {
          this.industries = result.content;
        },
        (error: HttpErrorResponse) => {
          if (error.status === 400) {
            const errorMessage = error.error as HttpErrorResponseMessages;
            errorMessage.reasons.forEach(reasons => {
              this.toastService.warning(reasons.errorMessage);
            });
          }
        });
  }

  getRegions() {
    if (this.userForm.controls.country.value) {
      this.regionService.getRegions(this.userForm.controls.country.value)
        .subscribe(
          result => {
            this.regions = result.content;
          },
          (error: HttpErrorResponse) => {
            if (error.status === 400) {
              const errorMessage = error.error as HttpErrorResponseMessages;
              errorMessage.reasons.forEach(reasons => {
                this.toastService.warning(reasons.errorMessage);
              });
            }
          });
    }
  }

  refreshRegions() {
    this.userForm.controls.region.setValue('');
    this.getRegions();
  }
  onRegionSelectionChanged() {
    this.userForm.controls.regionAbbrOrName.setValue(this.userForm.controls.region.value.abbreviation);
  }

  onSubmit() {
    Object.keys(this.userForm.controls).forEach((control: string) => {
      this.userForm.controls[control].markAsDirty();
    });
    if (this.userForm.controls.industry.value === 'Other (with text box)' && !this.userForm.controls.otherIndustry.value) {
      this.toastService.warning("Please enter an industry.");
    }
    if (this.userForm.invalid) {
      this.toastService.warning("Please fill in the required fields.");
      return;
    }
    this.applyForBusinessAccount();
  }

  applyForBusinessAccount() {
    const businessAccountRequest: ApplyForBusinessAccountRequest = {
      addressLine1: this.userForm.controls.addressLine1.value,
      addressLine2: this.userForm.controls.addressLine2.value,
      city: this.userForm.controls.city.value,
      companyName: this.userForm.controls.companyName.value,
      contactFirstName: this.userForm.controls.contactFirstName.value,
      contactLastName: this.userForm.controls.contactLastName.value,
      countryName: this.userForm.controls.country.value,
      email: this.userForm.controls.email.value,
      industry: this.userForm.controls.industry.value,
      phone: this.userForm.controls.phone.value,
      postalCode: this.userForm.controls.postalCode.value,
      regionName: this.userForm.controls.region.value,
      salutation: this.userForm.controls.salutation.value,
      title: this.userForm.controls.title.value,
    };
    this.accountService.applyForBusinessAccount(businessAccountRequest).subscribe(
      () => {
        this.toastService.success("Your business account application is successfully submitted.");
        this.router.navigate(['account/apply-business-account-complete']);
      },
      (error: HttpErrorResponse) => {
        if (error.status === 400) {
          const errorMessage = error.error as HttpErrorResponseMessages;
          errorMessage.reasons.forEach(reasons => {
            this.toastService.warning(reasons.errorMessage);
          });
        }
        this.toastService.warning("Your application could not be submitted at this time. Please either retry or contact us for more information.");
      });
  }
}
